import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { NoBusinessGuard } from "@/components/NoBusinessGuard";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Lightning, Sparkle, Trophy, Copy, Check, ArrowLeft,
  Star, BookmarkSimple, SpinnerGap
} from "@phosphor-icons/react";

const LOADING_MESSAGES = [
  "Generating Variation A...",
  "Crafting a different angle...",
  "Generating Variation B...",
  "Comparing hooks...",
  "Almost ready...",
];

export default function ABTestPage() {
  const { api, refreshUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [generating, setGenerating] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState("");
  const [result, setResult] = useState(null);
  const [winner, setWinner] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [copiedA, setCopiedA] = useState(false);
  const [copiedB, setCopiedB] = useState(false);
  const autoGenTriggered = useRef(false);

  const prefill = location.state?.abTest;

  // Loading message animation
  useEffect(() => {
    if (!generating) return;
    let idx = 0;
    setLoadingMsg(LOADING_MESSAGES[0]);
    const interval = setInterval(() => {
      idx = (idx + 1) % LOADING_MESSAGES.length;
      setLoadingMsg(LOADING_MESSAGES[idx]);
    }, 2500);
    return () => clearInterval(interval);
  }, [generating]);

  // Auto-generate on mount if prefill exists
  const handleGenerate = useCallback(async () => {
    if (!prefill) return;
    setGenerating(true);
    setResult(null);
    setWinner(null);
    setSaved(false);

    try {
      const resp = await api.post("/generate/ab-test", {
        topic: prefill.topic,
        research_text: prefill.research_text || "",
        content_type: prefill.content_type,
        platform: prefill.platform,
        audience: prefill.audience,
        goal: prefill.goal,
        cta: prefill.cta || "NONE",
        offer_asset: prefill.offer_asset || "NONE",
        tone_variations: prefill.tone_variations || [],
        length_constraint: prefill.length_constraint || "PLATFORM BEST LENGTH",
        ai_model: prefill.ai_model,
        file_ids: prefill.file_ids || [],
        strategy_profile_id: prefill.strategy_profile_id || null,
      });
      setResult(resp.data);
      toast.success("Two variations ready for comparison!");
      refreshUser();
    } catch (err) {
      const detail = err.response?.data?.detail || "A/B generation failed. Please try again.";
      toast.error(typeof detail === "string" ? detail : detail.message || "Generation failed");
    } finally {
      setGenerating(false);
    }
  }, [api, prefill, refreshUser]);

  useEffect(() => {
    if (prefill && !autoGenTriggered.current) {
      autoGenTriggered.current = true;
      handleGenerate();
    }
  }, [prefill, handleGenerate]);

  const handlePickWinner = async (pick) => {
    if (!result?.test_id) return;
    setWinner(pick);
    try {
      await api.post(`/generate/ab-test/${result.test_id}/pick-winner`, { winner: pick });
      toast.success(`Variation ${pick.toUpperCase()} selected as winner!`);
    } catch {
      toast.error("Failed to record winner.");
    }
  };

  const handleSaveWinner = async () => {
    if (!winner || !result) return;
    const variation = winner === "a" ? result.variation_a : result.variation_b;
    const inputs = result.inputs || prefill || {};
    setSaving(true);
    try {
      await api.post("/history", {
        title: variation.suggested_title || `A/B Winner: ${inputs.content_type} - ${inputs.topic?.substring(0, 60)}`,
        topic: inputs.topic || "",
        research_material: prefill?.research_text || "",
        content_type: inputs.content_type || "",
        platform: inputs.platform || "",
        audience: inputs.audience || "",
        goal: inputs.goal || "",
        cta: inputs.cta || "NONE",
        offer_asset: inputs.offer_asset || "NONE",
        tone_variations: inputs.tone_variations || [],
        length_constraint: inputs.length_constraint || "PLATFORM BEST LENGTH",
        ai_model_used: inputs.ai_model || "",
        generated_core_angle: variation.core_angle || "",
        generated_draft: variation.draft || "",
        generated_why_it_works: variation.why_it_works || "",
        generated_alternate_hooks: variation.alternate_hooks || [],
        generated_cta_variations: variation.cta_variations || [],
        strategy_profile_id: result.strategy_profile_used?.strategy_profile_id || null,
        strategy_profile_version: result.strategy_profile_used?.version || null,
      });
      setSaved(true);
      toast.success("Winner saved to history!");
      refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  const copyDraft = (text, variant) => {
    navigator.clipboard.writeText(text || "");
    if (variant === "a") { setCopiedA(true); setTimeout(() => setCopiedA(false), 2000); }
    else { setCopiedB(true); setTimeout(() => setCopiedB(false), 2000); }
    toast.success("Copied to clipboard!");
  };

  if (!prefill && !result) {
    return (
      <div className="min-h-screen bg-[var(--bg-base)]">
        <Header />
        <main className="max-w-2xl mx-auto px-4 py-16 text-center">
          <Lightning size={48} weight="duotone" className="text-[#FF4400] mx-auto mb-4" />
          <h1 className="font-['Cormorant_Garamond'] text-2xl font-bold text-[var(--text-primary)] mb-2">A/B Content Testing</h1>
          <p className="text-sm text-[var(--text-muted)] mb-6">Generate two variations of the same content to compare hooks and angles.</p>
          <Link to="/generate">
            <Button className="bg-[#FF4400] hover:bg-[#FF6633] text-white gap-2">
              <ArrowLeft size={16} /> Go to Generate Page
            </Button>
          </Link>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg-base)]">
      <Header />
      <NoBusinessGuard>
        <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10 space-y-6" data-testid="ab-test-page">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Link to="/generate" className="text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors">
                  <ArrowLeft size={18} />
                </Link>
                <h1 className="font-['Cormorant_Garamond'] text-2xl font-bold text-[var(--text-primary)]">A/B Variation Test</h1>
              </div>
              <p className="text-sm text-[var(--text-muted)]">
                {prefill?.content_type} &middot; {prefill?.platform} &middot; {prefill?.topic?.substring(0, 80)}
              </p>
            </div>
            {winner && !saved && (
              <Button
                onClick={handleSaveWinner}
                disabled={saving}
                className="bg-[#FF4400] hover:bg-[#FF6633] text-white gap-2"
                data-testid="ab-save-winner-btn"
              >
                {saving ? <SpinnerGap size={16} className="animate-spin" /> : <BookmarkSimple size={16} weight="fill" />}
                Save Winner to History
              </Button>
            )}
            {saved && (
              <span className="flex items-center gap-1.5 text-sm text-emerald-400 font-medium" data-testid="ab-saved-indicator">
                <Check size={16} weight="bold" /> Saved
              </span>
            )}
          </div>

          {/* Loading State */}
          {generating && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-12 text-center"
            >
              <Sparkle size={32} className="animate-spin text-[#FF4400] mx-auto mb-4" />
              <p className="text-sm text-[var(--text-secondary)] font-medium">{loadingMsg}</p>
              <p className="text-xs text-[var(--text-muted)] mt-2">Generating two distinct variations in parallel...</p>
            </motion.div>
          )}

          {/* Results: Side-by-side */}
          <AnimatePresence>
            {result && !generating && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="grid grid-cols-1 lg:grid-cols-2 gap-4"
                data-testid="ab-results"
              >
                {/* Variation A */}
                <VariationCard
                  label={result.variation_a?.label || "Variation A"}
                  variation={result.variation_a}
                  variant="a"
                  isWinner={winner === "a"}
                  isPicked={!!winner}
                  onPick={() => handlePickWinner("a")}
                  onCopy={() => copyDraft(result.variation_a?.draft, "a")}
                  copied={copiedA}
                />

                {/* Variation B */}
                <VariationCard
                  label={result.variation_b?.label || "Variation B"}
                  variation={result.variation_b}
                  variant="b"
                  isWinner={winner === "b"}
                  isPicked={!!winner}
                  onPick={() => handlePickWinner("b")}
                  onCopy={() => copyDraft(result.variation_b?.draft, "b")}
                  copied={copiedB}
                />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Regenerate */}
          {result && !generating && (
            <div className="flex justify-center">
              <Button
                onClick={handleGenerate}
                variant="outline"
                className="border-[var(--border-strong)] text-[var(--text-secondary)] hover:text-[#FF4400] hover:border-[#FF4400]/30 gap-2"
                data-testid="ab-regenerate-btn"
              >
                <Lightning size={16} /> Regenerate Both Variations
              </Button>
            </div>
          )}
        </main>
      </NoBusinessGuard>
    </div>
  );
}


function VariationCard({ label, variation, variant, isWinner, isPicked, onPick, onCopy, copied }) {
  if (!variation) return null;

  const borderClass = isWinner
    ? "border-[#FF4400] shadow-[0_0_20px_rgba(255,68,0,0.15)]"
    : isPicked
      ? "border-[var(--border)] opacity-60"
      : "border-[var(--border)] hover:border-[var(--border-strong)]";

  return (
    <div
      className={`bg-[var(--bg-card)] border rounded-xl p-5 transition-all ${borderClass}`}
      data-testid={`ab-variation-${variant}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
            variant === "a"
              ? "bg-blue-500/10 text-blue-400 border border-blue-500/20"
              : "bg-purple-500/10 text-purple-400 border border-purple-500/20"
          }`}>
            {variant.toUpperCase()}
          </span>
          <span className="text-sm font-semibold text-[var(--text-primary)]">{label}</span>
          {isWinner && (
            <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FF4400]/10 border border-[#FF4400]/20" data-testid={`ab-winner-badge-${variant}`}>
              <Trophy size={12} weight="fill" className="text-[#FF4400]" />
              <span className="text-[10px] font-semibold text-[#FF4400]">Winner</span>
            </span>
          )}
        </div>
        <button
          onClick={onCopy}
          className="flex items-center gap-1 px-2 py-1 rounded-md text-xs text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)] transition-colors"
          data-testid={`ab-copy-${variant}`}
        >
          {copied ? <Check size={12} className="text-emerald-400" /> : <Copy size={12} />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>

      {/* Core Angle */}
      {variation.core_angle && (
        <div className="mb-3 p-3 rounded-lg bg-[var(--bg-base)] border border-[var(--border)]">
          <p className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] font-semibold mb-1">Core Angle</p>
          <p className="text-sm text-[var(--text-secondary)]">{variation.core_angle}</p>
        </div>
      )}

      {/* Draft */}
      <div className="mb-4">
        <p className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] font-semibold mb-2">Draft</p>
        <div className="text-sm text-[var(--text-primary)] leading-relaxed whitespace-pre-wrap max-h-[400px] overflow-y-auto pr-2 custom-scroll">
          {variation.draft || "No draft generated."}
        </div>
      </div>

      {/* Why it Works */}
      {variation.why_it_works && (
        <div className="mb-4 p-3 rounded-lg bg-[var(--accent-subtle)]/30">
          <p className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] font-semibold mb-1">Why it Works</p>
          <p className="text-xs text-[var(--text-secondary)]">{variation.why_it_works}</p>
        </div>
      )}

      {/* Alternate Hooks */}
      {variation.alternate_hooks?.length > 0 && (
        <div className="mb-4">
          <p className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] font-semibold mb-1.5">Alternate Hooks</p>
          <ul className="space-y-1">
            {variation.alternate_hooks.map((hook, i) => (
              <li key={`hook-${i}-${hook.slice(0,20)}`} className="text-xs text-[var(--text-secondary)] pl-3 border-l-2 border-[var(--border)]">{hook}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Pick Winner */}
      {!isPicked && (
        <Button
          onClick={onPick}
          className={`w-full gap-2 ${
            variant === "a"
              ? "bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 border border-blue-500/20"
              : "bg-purple-500/10 hover:bg-purple-500/20 text-purple-400 border border-purple-500/20"
          }`}
          data-testid={`ab-pick-${variant}`}
        >
          <Star size={16} weight="fill" /> Pick as Winner
        </Button>
      )}
    </div>
  );
}
